import { useCallback, useEffect, useState } from 'react';

/** Classic is the long dashboard. MME is the calm tile home. */
export type LearnerHomeDesign = 'classic' | 'mme';

const STORAGE_KEY = 'persianist.learnerHomeDesign';
const ROW_OPEN_KEY = 'persianist.learnerHomeDesignRowOpen';
export const LEARNER_HOME_DESIGN_EVENT = 'persianist:learner-home-design';
/** Fired after the learner switches template, once the new theme is on screen. */
export const LEARNER_HOME_DESIGN_USER_CHANGED_EVENT = 'persianist:learner-home-design-user-changed';
export const LEARNER_HOME_DESIGN_ROW_EVENT = 'persianist:learner-home-design-row';

export function readLearnerHomeDesign(): LearnerHomeDesign {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'mme' ? 'mme' : 'classic';
  } catch {
    return 'classic';
  }
}

export function writeLearnerHomeDesign(design: LearnerHomeDesign): void {
  try {
    localStorage.setItem(STORAGE_KEY, design);
  } catch {
    /* private mode */
  }
  window.dispatchEvent(new CustomEvent(LEARNER_HOME_DESIGN_EVENT, { detail: design }));
}

export function useLearnerHomeDesign(): readonly [LearnerHomeDesign, (design: LearnerHomeDesign) => void] {
  const [design, setDesign] = useState<LearnerHomeDesign>(readLearnerHomeDesign);

  useEffect(() => {
    const sync = () => setDesign(readLearnerHomeDesign());
    window.addEventListener(LEARNER_HOME_DESIGN_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(LEARNER_HOME_DESIGN_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const update = useCallback((next: LearnerHomeDesign) => {
    writeLearnerHomeDesign(next);
    setDesign(next);
  }, []);

  return [design, update] as const;
}

/** First visit keeps the theme row open. Later visits use the last open or closed choice. */
export function readLearnerHomeDesignRowOpen(): boolean {
  try {
    return localStorage.getItem(ROW_OPEN_KEY) !== '0';
  } catch {
    return true;
  }
}

export function writeLearnerHomeDesignRowOpen(open: boolean): void {
  try {
    localStorage.setItem(ROW_OPEN_KEY, open ? '1' : '0');
  } catch {
    /* private mode */
  }
  window.dispatchEvent(new CustomEvent(LEARNER_HOME_DESIGN_ROW_EVENT, { detail: open }));
}

export function useLearnerHomeDesignRowOpen(): readonly [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(readLearnerHomeDesignRowOpen);

  useEffect(() => {
    const sync = () => setOpen(readLearnerHomeDesignRowOpen());
    window.addEventListener(LEARNER_HOME_DESIGN_ROW_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(LEARNER_HOME_DESIGN_ROW_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const update = useCallback((next: boolean) => {
    writeLearnerHomeDesignRowOpen(next);
    setOpen(next);
  }, []);

  return [open, update] as const;
}
